import { useProfileStore } from "@features/profile/profile.store";
import { supabase } from "@shared/lib/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export function useAvatar() {
  const profile = useProfileStore((state) => state.profile);
  const queryClient = useQueryClient();

  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (!profile) throw new Error("Not authenticated");

      const ext = file.name.split(".").pop();
      const path = `${profile.id}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(path, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from("avatars").getPublicUrl(path);

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: data.publicUrl })
        .eq("user_id", profile.user_id);

      if (updateError) throw updateError;

      return data.publicUrl;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Avatar updated");
    },
    onError: () => {
      toast.error("Failed to upload avatar");
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!profile) throw new Error("Not authenticated");

      const { error: removeError } = await supabase.storage
        .from("avatars")
        .remove([
          `${profile.id}/avatar.jpg`,
          `${profile.id}/avatar.png`,
          `${profile.id}/avatar.webp`,
        ]);

      if (removeError) throw removeError;

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("user_id", profile.user_id);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Avatar removed");
    },
    onError: () => {
      toast.error("Failed to remove avatar");
    },
  });

  return { upload, remove };
}
